import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { FaLink } from 'react-icons/fa';
import { toastError } from '../utils/toasts';
import SelectTicketsTable, {
  TicketSelection,
} from '../components/SelectTicketsTable';
import {
  setTicketsSelected, useCheckCouponValidationMutation, useGetAllTicketsQuery, useReserveTicketsMutation,
} from '../store';
import Button from '../components/atoms/Button';
import TicketDTO, { TicketReservation } from '../models/ticket';
import OrdersTableSkeleton from '../components/OrdersTableSkeleton';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import useGetUserRole from '../hooks/useGetUserRole';
import IconInfoCard from '../components/atoms/IconInfoCard';
import Select from '../components/atoms/Select/Select';
import { parseFriendlyDate } from '../utils/dates';
import EmptyTable from '../components/Table/EmptyTable';
import isMobile from '../utils/isMobile';
import useGetUserEventData from '../hooks/useGetUserEventData';
import getCustomTicketLabel from '../utils/getCustomTicketLabel';
import { trackAddToCart } from '../utils/metaPixelTracking';

const SelectTicketsPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigateWithQuery();
  const location = useLocation();
  const isBackoffice = location.pathname.includes('admin');
  const isManualTickets = location.pathname.includes('manual');
  const { event } = useGetSelectedEvent({ slug: !isBackoffice });
  const { data: eventTickets, isLoading: ticketsLoading } = useGetAllTicketsQuery({ businessId: event?.business_id || '', eventId: event?.id || '', publicSaleAvailable: !isBackoffice }, { skip: !event });
  const role = useGetUserRole({ skip: !isBackoffice });
  const userEventData = useGetUserEventData(event?.id || '', { skip: !isBackoffice || !event });
  const [allTickets, setAllTickets] = useState<TicketDTO[]>([]);
  const [entryTickets, setEntryTickets] = useState<TicketDTO[]>([]);
  const [extraTickets, setExtraTickets] = useState<TicketDTO[]>([]);
  const [warning, setWarning] = useState(false);
  const [ticketSelection, setTicketsSelection] = useState<TicketSelection[]>([]);
  const [extraSelection, setExtraSelection] = useState<TicketSelection[]>([]);
  const [reserveTickets, resultsReserve] = useReserveTicketsMutation();
  const [ticketKey, setTicketKey] = useState(0);
  const [selectedShowtimeId, setSelectedShowtimeId] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState<string | null>(null);
  const [checkCouponValidation, couponValidationResults] = useCheckCouponValidationMutation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const coupon = params.get('coupon');
    setCouponCode(coupon);
  }, [location.search]);

  useEffect(() => {
    if (event?.has_numerated_seats) {
      navigate('../seats');
    }
  }, [event]);

  const validateCoupon = (codeVal: string) => {
    checkCouponValidation({
      businessId: event?.business_id || '',
      eventId: event?.id || '',
      amount: 0,
      couponCode: codeVal,
    });
  };

  useEffect(() => {
    if (couponCode) {
      validateCoupon(couponCode);
    }
  }, [couponCode]);

  const isSellerGroupCorrect = (ticket: TicketDTO) => {
    if (!ticket.seller_groups || ticket.seller_groups.length === 0) {
      return true;
    }
    return ticket.seller_groups.some((sg) => sg.id === userEventData?.seller_group_id);
  };

  useEffect(() => {
    if (isBackoffice) {
      setAllTickets(
        eventTickets?.filter((t) => (role === 'SELLER' && t.is_seller_ticket && userEventData && isSellerGroupCorrect(t) && !t.is_only_admin)
          || (role === 'ADMIN' || role === 'ROOT')) || [],
      );
    } else {
      setAllTickets(
        eventTickets || [],
      );
    }
    setTicketKey((prevKey) => prevKey + 1);
  }, [eventTickets]);

  useEffect(() => {
    localStorage.removeItem('formItemsData');
    localStorage.removeItem(`${event?.id}_orderItemData`);
    localStorage.removeItem(`${event?.id}_selectedTickets`);
    localStorage.removeItem(`${event?.id}_email`);
    localStorage.removeItem(`${event?.id}_coupon`);
  }, []);

  useEffect(() => {
    if (selectedShowtimeId) {
      setEntryTickets(allTickets.filter((t) => !t.is_extra && t.showtime_id === selectedShowtimeId));
    } else {
      setEntryTickets(allTickets.filter((t) => !t.is_extra));
    }
    setExtraTickets(allTickets.filter((t) => t.is_extra));
  }, [allTickets, selectedShowtimeId]);

  useEffect(() => {
    if (ticketSelection.length > 0 || extraSelection.length > 0) {
      setWarning(false);
    }
  }, [ticketSelection, extraSelection]);

  useEffect(() => {
    if (role === 'SELLER' && !event && isBackoffice) {
      navigate('../../../');
    } else if (!event) {
      navigate('../../');
    }
  }, [event, navigate]);

  if (!event) {
    return <div>no event</div>;
  }

  const handleTicketsChange = (updatedSelection: TicketSelection[]) => {
    setTicketsSelection(updatedSelection);
  };

  const handleExtrasChange = (updatedSelection: TicketSelection[]) => {
    setExtraSelection(updatedSelection);
  };

  const handleClickNext = async () => {
    if (ticketSelection.length === 0 && extraSelection.length === 0) {
      return setWarning(true);
    }
    const reservations: TicketReservation[] = ticketSelection.map((ts) => ({
      ticket_id: ts.ticket.id,
      amount: ts.quantity,
    }));
    const result = await reserveTickets({
      businessId: event.business_id,
      eventId: event.id,
      reservations,
    });

    let errorMsg = 'Ocurrió un error';
    if ('data' in result) {
      if (result.data.data?.valid) {
        if (!isBackoffice) {
          const allSelections = [...ticketSelection, ...extraSelection];
          trackAddToCart({
            content_name: event.name,
            content_type: 'product',
            content_ids: Array.from(new Set(allSelections.map((ts) => ts.ticket.id))),
            value: allSelections.reduce((sum, ts) => sum + ts.ticket.price * ts.quantity, 0),
            num_items: allSelections.reduce((sum, ts) => sum + ts.quantity, 0),
          });
        }
        localStorage.setItem(`${event?.id}_selectedTickets`, JSON.stringify([...ticketSelection, ...extraSelection]));
        dispatch(setTicketsSelected([...ticketSelection, ...extraSelection]));
        return navigate('../entry-data', { replace: true });
      }
      if (result.data.data?.valid === false) {
        errorMsg = `${getCustomTicketLabel(event, { plural: true, casing: 'first_letter' })} seleccionadas se agotaron`;
      }
    } else {
      console.error('error reserving tickets', result);
    }
    toastError(errorMsg);
    return '';
  };

  const handleBack = () => {
    if (role === 'SELLER') {
      navigate('../../../', { replace: true });
    } else if (isManualTickets) {
      navigate('../../../', { replace: true });
    } else {
      navigate('../../', { replace: true });
    }
  };

  if (event?.is_approved === false) {
    return (
      <div className='my-6 flex flex-col gap-6'>
        <IconInfoCard
          title="Evento no aprobado"
          info="El evento no ha sido aprobado aún"
          icon={FaLink}
          iconColor="text-primary-500"
        />
      </div>
    );
  }

  if (isBackoffice && event?.seller_sales_enabled === false && role === 'SELLER') {
    return (
      <div className='my-6 flex flex-col gap-6'>
        <IconInfoCard
          title="No se puede generar entradas"
          info="Las ventas de entradas por vendedores no están habilitadas para este evento"
          icon={FaLink}
          iconColor="text-primary-500"
        />
        <div className="min-w-md mx-auto">
          <Button variant="primary" onClick={handleBack}>
            Atrás
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {event?.has_showtimes && event?.showtimes && <div className='flex items-center gap-4 mb-4'>
        {!isMobile() && <p className='w-full max-w-md font-medium'>Seleccioná una función</p>}
        <Select
          label={isMobile() ? 'Seleccioná una función' : ''}
          id="showtime"
          options={event?.showtimes?.filter((showtime) => showtime.active && showtime.ends_at > new Date().toISOString()).sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()).map((showtime) => ({
            label: parseFriendlyDate(showtime.starts_at, true, true),
            value: showtime.id,
          }))}
          onChange={(value) => setSelectedShowtimeId(value.target.value)}
        />
      </div>
      }
      {ticketsLoading && <div className="glass-surface"><OrdersTableSkeleton /></div>}
      {entryTickets.length > 0 && <div>
        {(!ticketsLoading && (!event?.has_showtimes || selectedShowtimeId)) && <SelectTicketsTable
          tickets={entryTickets} // pendiente resetear el contador cuando se agoten
          onTicketsChange={(updatedTicketsSelection) => handleTicketsChange(updatedTicketsSelection)}
          key={ticketKey}
          coupon={couponValidationResults.data?.data?.coupon || undefined}
          title={`Entradas ${event?.has_showtimes && selectedShowtimeId ? `- ${parseFriendlyDate(event?.showtimes?.find((st) => st.id === selectedShowtimeId)?.starts_at ?? '', false, true)}` : ''}`}
        />}
      </div>}
      {!ticketsLoading && entryTickets.length === 0 && !event?.has_showtimes && <div>
        <IconInfoCard
          title="Aún no hay entradas creadas"
          info="Aquí aparecerán todas las entradas creadas para el evento"
          icon={FaLink}
          iconColor="text-primary-500"
        />
      </div>}
      {event?.has_showtimes && !selectedShowtimeId && <div>
        <EmptyTable
          title="Primero debes elegir una función"
          info="Una vez elegida la función verás entradas disponibles de cada una"
          icon={FaLink}
          iconColor="text-primary-500"
        />
      </div>}
      {event?.has_showtimes && selectedShowtimeId && entryTickets.length === 0 && <div>
        <EmptyTable
          title="No hay entradas disponibles para esta función"
          info="Selecciona otra función para ver las entradas disponibles"
          icon={FaLink}
          iconColor="text-primary-500"
        />
      </div>}
      {(!ticketsLoading && extraTickets.length > 0) && <div className="mt-5">
        <SelectTicketsTable
          tickets={extraTickets} // pendiente resetear el contador cuando se agoten
          onTicketsChange={(updatedExtrasSelection) => handleExtrasChange(updatedExtrasSelection)}
          key={ticketKey}
          coupon={couponValidationResults.data?.data?.coupon || undefined}
          title="Extras"
        />
      </div>}
      {warning && (
        <div className="text-red-500 text-center mt-2">
          Debes seleccionar al menos una entrada
        </div>
      )}
      <div className="flex flex-col-reverse md:flex-row gap-4 justify-center mt-4">
        <Button variant="secondary" onClick={handleBack}>
          Volver
        </Button>
        {!ticketsLoading && entryTickets.length > 0 && <Button
          variant="primary"
          onClick={handleClickNext}
          loading={resultsReserve.isLoading}
        >
          Siguiente
        </Button>}
      </div>
    </div>
  );
};

export default SelectTicketsPage;
